import { useEffect, useState, useCallback } from "react";
import { api, formatNumber, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function SettingsPage() {
  const [catalog, setCatalog] = useState([]);
  const [planned, setPlanned] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, pl] = await Promise.all([
        api.get("/items/catalog"),
        api.get("/items/planned"),
      ]);
      setCatalog(cat.data);
      setPlanned(pl.data);
      const d = {};
      cat.data.forEach((it) => {
        it.sub_items.forEach((s) => {
          const key = `${it.key}:${s.key}`;
          d[key] = String(pl.data[key] || 0);
        });
      });
      setDrafts(d);
    } catch {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (itemKey, subKey) => {
    const compositeKey = `${itemKey}:${subKey}`;
    const val = parseFloat(drafts[compositeKey] || "0");
    if (isNaN(val) || val < 0) {
      toast.error("Geçerli bir sayı girin");
      return;
    }
    setSavingKey(compositeKey);
    try {
      await api.put("/items/planned", {
        item_key: itemKey,
        sub_item_key: subKey,
        planned: val,
      });
      setPlanned((p) => ({ ...p, [compositeKey]: val }));
      toast.success("Planlanan metraj güncellendi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-sky-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-sky-500 mb-2">Ayarlar</div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight font-display">Planlanan Metrajlar</h1>
        <p className="text-slate-600 mt-2">Proje başlangıcında belirlenen hedef metrajları buradan girin/güncelleyin.</p>
      </div>

      <Accordion type="multiple" className="space-y-3">
        {catalog.map((item) => {
          const itemTotal = item.sub_items.reduce((s, x) => s + (planned[`${item.key}:${x.key}`] || 0), 0);
          return (
            <AccordionItem key={item.key} value={item.key} className="site-card rounded-lg border-0">
              <AccordionTrigger className="px-5 py-4 hover:no-underline" data-testid={`settings-item-${item.key}`}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div
                    className="w-10 h-10 rounded-md flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-black text-slate-900 truncate">{item.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{item.sub_items.length} alt kırılım</div>
                  </div>
                  <div className="text-right pr-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Toplam Planlanan</div>
                    <div className="font-mono-num font-black text-lg text-slate-900">{formatNumber(itemTotal)}</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  {item.sub_items.map((sub) => {
                    const key = `${item.key}:${sub.key}`;
                    const value = drafts[key] ?? "";
                    const savedValue = planned[key] || 0;
                    const dirty = parseFloat(value || "0") !== savedValue;
                    return (
                      <div key={sub.key} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-center py-2" data-testid={`settings-sub-${sub.key}`}>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{sub.name}</div>
                          <div className="text-xs text-slate-500">Birim: {sub.unit}</div>
                        </div>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={value}
                          onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                          className="w-full sm:w-40 h-11 border-2 border-slate-300 font-mono-num text-right"
                          data-testid={`settings-input-${item.key}-${sub.key}`}
                        />
                        <Button
                          className="bg-sky-500 hover:bg-sky-600 text-white h-11 disabled:bg-slate-300"
                          disabled={!dirty || savingKey === key}
                          onClick={() => save(item.key, sub.key)}
                          data-testid={`settings-save-${item.key}-${sub.key}`}
                        >
                          {savingKey === key ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-2" />Kaydet</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
